import { useMemo, useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from 'recharts';
import { format, startOfMonth, endOfMonth, eachMonthOfInterval, subMonths, addMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TrendingUp, TrendingDown, DollarSign } from 'lucide-react';
import { YearSelector } from '@/components/common/YearSelector';
import { getEffectiveTransactionDate, parseTransactionDateUTC } from '@/lib/utils';
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { CustomTooltipProps, TooltipPayload } from "@/types/recharts";

interface Transaction {
  id: string;
  tipo: "entrada" | "saida";
  valor: number;
  data: string;
  foi_paga: boolean;
  ignorar_transacao: boolean;
  conta_id?: string;
  data_pagamento?: string;
}

interface CashFlowReportProps {
  transactions: Transaction[];
  selectedDate?: Date;
}

export function CashFlowReport({ transactions, selectedDate }: CashFlowReportProps) {
  const currentDate = selectedDate || new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const viewMode: '6months' | '12months' = '12months'; // Exibir 12 meses

  // Buscar contas para calcular datas efetivas
  const { data: accounts = [] } = useQuery({
    queryKey: ['contas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contas')
        .select('*')
        .eq('ativo', true);
      
      if (error) throw error;
      return data || [];
    }
  });

  const cashFlowData = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return { monthlyData: [], summary: { totalIncome: 0, totalExpenses: 0, netFlow: 0 } };
    }

    const monthsToShow = viewMode === '12months' ? 12 : 6;
    const endDate = new Date(selectedYear, 11, 31); // Dezembro do ano selecionado
    const startDate = subMonths(endDate, monthsToShow - 1);
    
    const months = eachMonthOfInterval({ start: startDate, end: endDate });

    // Data efetiva para fluxo de caixa:
    // - Despesas de cartão de crédito: usar data da compra (t.data)
    // - Demais casos: usar getEffectiveTransactionDate
    const getCashFlowDate = (t: Transaction): Date | null => {
      const account = accounts.find(acc => acc.id === t.conta_id);
      if (t.tipo === 'saida' && account?.tipo === 'cartao_credito') {
        return parseTransactionDateUTC(t.data);
      }
      // @ts-expect-error - Transaction type mismatch from database
      return getEffectiveTransactionDate(t, accounts);
    };
    
    const monthlyData = months.map(month => {
      // const monthStart = startOfMonth(month);
      // const monthEnd = endOfMonth(month);
      
      const monthTransactions = transactions.filter(t => {
        if (!t.data || t.ignorar_transacao) return false;
        const transactionDate = getCashFlowDate(t);
        
        if (!transactionDate || !(transactionDate instanceof Date) || isNaN(transactionDate.getTime())) {
          return false;
        }
        
        // Verificar se a data da transação é futura
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Para transações futuras, considerar apenas se estiverem marcadas como pagas
        // Restringe essa regra às RECEITAS, permitindo despesas aparecerem assim que criadas
        if (t.tipo === 'entrada' && transactionDate > today && !t.foi_paga) {
          return false;
        }
        
        // Usar comparação de mês e ano para maior robustez
        return transactionDate.getMonth() === month.getMonth() && transactionDate.getFullYear() === month.getFullYear();
      });
      
      const income = monthTransactions
        .filter(t => t.tipo === 'entrada' && t.foi_paga)
        .reduce((sum, t) => sum + Number(t.valor), 0);
      
      const expenses = monthTransactions
        .filter(t => t.tipo === 'saida')
        .reduce((sum, t) => sum + Number(t.valor), 0);
      
      const netFlow = income - expenses;
      
      return {
        month: format(month, 'MMM', { locale: ptBR }),
        fullMonth: format(month, 'MMMM yyyy', { locale: ptBR }),
        income,
        expenses,
        netFlow,
        date: month
      };
    });
    
    const summary = {
      totalIncome: monthlyData.reduce((sum, m) => sum + m.income, 0),
      totalExpenses: monthlyData.reduce((sum, m) => sum + m.expenses, 0),
      netFlow: monthlyData.reduce((sum, m) => sum + m.netFlow, 0)
    };
    
    return { monthlyData, summary };
  }, [transactions, selectedYear, viewMode, accounts]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium mb-2">{label}</p>
          {payload.map((entry: TooltipPayload, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {(entry.name as string) ||
               (entry.dataKey === 'income' ? 'Receitas' :
                entry.dataKey === 'expenses' ? 'Despesas' :
                entry.dataKey === 'netFlow' ? 'Fluxo Líquido' : (entry.dataKey as string))}: {formatCurrency(entry.value as number)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base font-medium text-muted-foreground">
              Total Receitas
            </CardTitle>
            <div className="p-2 rounded-md bg-success text-success-foreground">
              <TrendingUp className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-base font-bold text-success">
              {formatCurrency(cashFlowData.summary.totalIncome)}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Entradas do período
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base font-medium text-muted-foreground">
              Total Despesas
            </CardTitle>
            <div className="p-2 rounded-md bg-destructive text-destructive-foreground">
              <TrendingDown className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-base font-bold text-destructive">
              {formatCurrency(cashFlowData.summary.totalExpenses)}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Saídas do período
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-base font-medium text-muted-foreground">
              Fluxo Líquido
            </CardTitle>
            <div className={`p-2 rounded-md ${
              cashFlowData.summary.netFlow >= 0 
                ? 'bg-success text-success-foreground' 
                : 'bg-destructive text-destructive-foreground'
            }`}>
              <DollarSign className="h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent>
            <div className={`text-base font-bold ${
              cashFlowData.summary.netFlow >= 0 ? 'text-success' : 'text-destructive'
            }`}>
              {formatCurrency(cashFlowData.summary.netFlow)}
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {cashFlowData.summary.netFlow >= 0 ? 'Resultado positivo' : 'Resultado negativo'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex justify-center">
        <YearSelector
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
        />
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Fluxo de Caixa {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={cashFlowData.monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  tickFormatter={(value) => {
                    if (value >= 1000) {
                      return `R$ ${(value / 1000).toFixed(0)}k`;
                    }
                    return `R$ ${value}`;
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="income"
                  stackId="1"
                  stroke="hsl(var(--success))"
                  fill="hsl(var(--success))"
                  fillOpacity={0.3}
                  name="Receitas"
                />
                <Area
                  type="monotone"
                  dataKey="expenses"
                  stackId="2"
                  stroke="hsl(var(--destructive))"
                  fill="hsl(var(--destructive))"
                  fillOpacity={0.3}
                  name="Despesas"
                />
                <Line
                  type="monotone"
                  dataKey="netFlow"
                  stroke="hsl(var(--primary))"
                  strokeWidth={3}
                  name="Fluxo Líquido"
                  dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2, r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Details */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Detalhes Mensais</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {cashFlowData.monthlyData.map((month, index) => (
              <div key={index} className="py-3 border-b border-border last:border-b-0">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium capitalize text-sm sm:text-base">{month.fullMonth}</span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs sm:text-sm">
                  <div className="text-center">
                    <div className="text-muted-foreground mb-1">Receitas</div>
                    <span className="text-success font-medium">+{formatCurrency(month.income)}</span>
                  </div>
                  <div className="text-center">
                    <div className="text-muted-foreground mb-1">Despesas</div>
                    <span className="text-destructive font-medium">-{formatCurrency(month.expenses)}</span>
                  </div>
                  <div className="text-center">
                    <div className="text-muted-foreground mb-1">Líquido</div>
                    <span className={`font-bold ${
                      month.netFlow >= 0 ? 'text-success' : 'text-destructive'
                    }`}>
                      {month.netFlow >= 0 ? '+' : ''}{formatCurrency(month.netFlow)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}